import { Layout } from "@/components/layout";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { UploadCloud, FileJson, CheckCircle, Loader2, Database } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth";
import { useLocation } from "wouter";

const BASE_URL = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

async function fetchWithAuth(path: string, opts?: RequestInit) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

type Track = "ppl" | "cpl" | "pstar" | "roca";

const TRACKS: { key: Track; label: string; fullName: string; color: string; borderColor: string; bgColor: string; badgeClass: string }[] = [
  { key: "ppl", label: "PPL", fullName: "Private Pilot Licence", color: "text-blue-400", borderColor: "border-blue-500/40", bgColor: "bg-blue-500/10", badgeClass: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { key: "cpl", label: "CPL", fullName: "Commercial Pilot Licence", color: "text-primary", borderColor: "border-primary/40", bgColor: "bg-primary/10", badgeClass: "bg-primary/20 text-primary border-primary/30" },
  { key: "pstar", label: "PSTAR", fullName: "Student Pilot (PSTAR)", color: "text-sky-400", borderColor: "border-sky-500/40", bgColor: "bg-sky-500/10", badgeClass: "bg-sky-500/20 text-sky-400 border-sky-500/30" },
  { key: "roca", label: "ROC-A", fullName: "Radio Operator Certificate — Aviation", color: "text-amber-400", borderColor: "border-amber-500/40", bgColor: "bg-amber-500/10", badgeClass: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
];

const sampleJson = `[
  {
    "text": "What is the minimum flight visibility for VFR flight in Class C airspace?",
    "options": [
      "1 mile",
      "3 miles",
      "5 miles",
      "Not applicable"
    ],
    "correctOptionIndex": 1,
    "explanation": "VFR flight in Class C airspace requires 3 miles visibility.",
    "category": "Air Law",
    "difficulty": "medium"
  }
]`;

export default function Import() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (user && !["admin", "master_admin"].includes(user.role)) setLocation("/");
  }, [user, setLocation]);

  const [activeTrack, setActiveTrack] = useState<Track>("ppl");
  const [jsonInput, setJsonInput] = useState("");
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: bankStats, isLoading: statsLoading } = useQuery<Record<Track, number>>({
    queryKey: ["question-bank-stats"],
    queryFn: () => fetchWithAuth("/api/admin/questions/bank-stats"),
    refetchInterval: 10000,
  });

  const importMutation = useMutation({
    mutationFn: async (questions: unknown[]) => {
      // Inject the selected track into every question
      const tagged = questions.map(q => ({
        ...(q as object),
        licenseTrack: activeTrack,
      }));
      return fetchWithAuth("/api/questions/bulk", {
        method: "POST",
        body: JSON.stringify({ questions: tagged }),
      });
    },
    onSuccess: (result: { count?: number; message?: string }) => {
      toast({
        title: "Import successful",
        description: result.message ?? `Imported ${result.count ?? 0} questions into the ${activeTrack.toUpperCase()} bank.`,
      });
      setJsonInput("");
      qc.invalidateQueries({ queryKey: ["question-bank-stats"] });
      qc.invalidateQueries({ queryKey: ["track-bank-counts"] });
    },
    onError: (err: Error) => {
      toast({ title: "Import failed", description: err.message, variant: "destructive" });
    },
  });

  function handleImport() {
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonInput);
    } catch (err) {
      console.warn("[import] JSON parse failed:", err);
      toast({ title: "Invalid JSON", description: "Please ensure your input is valid JSON.", variant: "destructive" });
      return;
    }
    const questions = Array.isArray(parsed) ? parsed : [parsed];
    importMutation.mutate(questions);
  }

  const active = TRACKS.find(t => t.key === activeTrack)!;
  const totalQuestions = bankStats ? Object.values(bankStats).reduce((a, b) => a + b, 0) : 0;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Question Bank</h1>
            <p className="text-muted-foreground font-mono text-sm mt-1">MANAGE & IMPORT QUESTIONS PER LICENCE TRACK</p>
          </div>
          <div className="flex items-center gap-2 bg-secondary px-4 py-2 rounded-md border border-border">
            <Database className="w-4 h-4 text-primary" />
            <span className="text-sm font-mono">
              TOTAL: <span className="text-primary font-bold">{statsLoading ? "…" : totalQuestions.toLocaleString()}</span>
            </span>
          </div>
        </div>

        {/* Track tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {TRACKS.map(t => {
            const count = bankStats?.[t.key] ?? 0;
            const isActive = activeTrack === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTrack(t.key)}
                className={cn(
                  "relative rounded-lg border p-4 text-left transition-all",
                  isActive
                    ? cn(t.borderColor, t.bgColor, "ring-1", t.borderColor)
                    : "border-border bg-card hover:border-border/80 hover:bg-card/80"
                )}
              >
                <div className={cn("text-xs font-mono font-bold tracking-widest mb-1", isActive ? t.color : "text-muted-foreground")}>
                  {t.label}
                </div>
                <div className={cn("text-2xl font-bold font-mono", isActive ? t.color : "text-foreground")}>
                  {statsLoading ? "—" : count.toLocaleString()}
                </div>
                <div className="text-[10px] text-muted-foreground font-mono mt-0.5 truncate">{t.fullName}</div>
                {isActive && (
                  <div className={cn("absolute top-2 right-2 w-1.5 h-1.5 rounded-full", t.color.replace("text-", "bg-"))} />
                )}
              </button>
            );
          })}
        </div>

        {/* Active bank header */}
        <div className={cn("border rounded-lg p-4 flex items-center gap-3", active.borderColor, active.bgColor)}>
          <CheckCircle className={cn("w-5 h-5 shrink-0", active.color)} />
          <div>
            <div className={cn("font-mono font-bold text-sm tracking-wide", active.color)}>
              {active.label} QUESTION BANK — ACTIVE
            </div>
            <div className="text-xs text-muted-foreground font-mono">
              {bankStats?.[activeTrack] ?? 0} questions loaded · All imports below will go into this bank
            </div>
          </div>
        </div>

        {/* Import form + format spec */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className={cn("border bg-card", active.borderColor)}>
            <CardContent className="pt-5 space-y-4">
              <div className="flex items-center gap-2">
                <UploadCloud className={cn("w-5 h-5", active.color)} />
                <div>
                  <div className="font-bold font-mono text-sm">Import into {active.label} Bank</div>
                  <div className="text-xs text-muted-foreground font-mono">{active.fullName} · JSON array payload</div>
                </div>
              </div>
              <textarea
                placeholder="Paste JSON array here..."
                className="w-full font-mono text-xs h-64 bg-background border border-input rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
              />
              <div className="text-[10px] text-muted-foreground font-mono">
                licenseTrack will automatically be set to <span className={cn("font-bold", active.color)}>{activeTrack.toUpperCase()}</span> for all imported questions.
              </div>
              <Button
                onClick={handleImport}
                disabled={!jsonInput.trim() || importMutation.isPending}
                className={cn("w-full font-mono text-xs uppercase tracking-widest")}
              >
                {importMutation.isPending
                  ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Transmitting…</>
                  : <><UploadCloud className="h-3.5 w-3.5 mr-2" />Import into {active.label} Bank</>}
              </Button>
            </CardContent>
          </Card>

          <Card className="border-border bg-card/50">
            <CardContent className="pt-5 space-y-4">
              <div className="flex items-center gap-2">
                <FileJson className="w-5 h-5 text-muted-foreground" />
                <div>
                  <div className="font-bold font-mono text-sm">Format Specification</div>
                  <div className="text-xs text-muted-foreground font-mono">Required schema for incoming data</div>
                </div>
              </div>
              <div className="bg-background rounded-md p-4 border border-border overflow-x-auto">
                <pre className="text-xs font-mono text-primary whitespace-pre-wrap">{sampleJson}</pre>
              </div>
              <ul className="space-y-1.5 text-xs text-muted-foreground font-mono">
                <li>→ JSON array of question objects</li>
                <li>→ <code className="text-foreground bg-secondary px-1 py-0.5 rounded">text</code> — question text (string)</li>
                <li>→ <code className="text-foreground bg-secondary px-1 py-0.5 rounded">options</code> — array of <strong>4</strong> MCQ strings or <strong>2</strong> for True/False</li>
                <li>→ <code className="text-foreground bg-secondary px-1 py-0.5 rounded">correctOptionIndex</code> — 0–1 (T/F) or 0–3 (MCQ)</li>
                <li>→ <code className="text-foreground bg-secondary px-1 py-0.5 rounded">text_fr</code>, <code className="text-foreground bg-secondary px-1 py-0.5 rounded">options_fr</code>, <code className="text-foreground bg-secondary px-1 py-0.5 rounded">explanation_fr</code> — optional French (same option count as English)</li>
                <li>→ <code className="text-foreground bg-secondary px-1 py-0.5 rounded">explanation</code> — why the answer is correct</li>
                <li>→ <code className="text-foreground bg-secondary px-1 py-0.5 rounded">category</code> — subject area (string)</li>
                <li>→ <code className="text-foreground bg-secondary px-1 py-0.5 rounded">difficulty</code> — easy / medium / hard (optional)</li>
                <li className={cn("font-semibold", active.color)}>→ licenseTrack is auto-set to {activeTrack.toUpperCase()} (you don't need to include it)</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Per-bank breakdown */}
        <Card className="border-border bg-card">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-4 h-4 text-muted-foreground" />
              <div className="font-bold font-mono text-sm text-muted-foreground uppercase tracking-widest">Bank Summary</div>
            </div>
            <div className="space-y-3">
              {TRACKS.map(t => {
                const count = bankStats?.[t.key] ?? 0;
                const pct = totalQuestions > 0 ? (count / totalQuestions) * 100 : 0;
                return (
                  <div key={t.key} className="space-y-1">
                    <div className="flex items-center justify-between text-xs font-mono">
                      <div className="flex items-center gap-2">
                        <span className={cn("font-bold", t.color)}>{t.label}</span>
                        <span className="text-muted-foreground">{t.fullName}</span>
                      </div>
                      <span className={cn("font-bold", t.color)}>{count.toLocaleString()} Q</span>
                    </div>
                    <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                      <div
                        className={cn("h-full rounded-full transition-all duration-500", t.color.replace("text-", "bg-"))}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
